import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as assetSaleReceiptAPI from '../../api/PurchaseAPI/assetSaleReceiptAPI';

// Async Thunks
// =============================================

export const fetchRequestItems = createAsyncThunk(
    'assetSaleReceipt/fetchRequestItems',
    async (params, { rejectWithValue }) => {
        try {
            const response = await assetSaleReceiptAPI.getRequestItems(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Request Numbers');
        }
    }
);

export const fetchAssetSaleItemRecDetails = createAsyncThunk(
    'assetSaleReceipt/fetchAssetSaleItemRecDetails',
    async (reqno, { rejectWithValue }) => {
        try {
            const response = await assetSaleReceiptAPI.getAssetSaleItemRecDetails(reqno);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch item details');
        }
    }
);

export const fetchAssetPartyDetails = createAsyncThunk(
    'assetSaleReceipt/fetchAssetPartyDetails',
    async (reqno, { rejectWithValue }) => {
        try {
            const response = await assetSaleReceiptAPI.getAssetPartyDetails(reqno);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch party details');
        }
    }
);

export const fetchBankDetails = createAsyncThunk(
    'assetSaleReceipt/fetchBankDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await assetSaleReceiptAPI.getBankDetails();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Bank Details');
        }
    }
);

export const saveAssetSaleReceiptData = createAsyncThunk(
    'assetSaleReceipt/saveAssetSaleReceiptData',
    async (params, { rejectWithValue }) => {
        try {
            const response = await assetSaleReceiptAPI.saveAssetSaleReceiptData(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save Asset Sale Receipt');
        }
    }
);

// Initial State
// =============
const initialState = {
    requestItems: [],
    itemGrid: [],
    partyDetails: null, // { PartyName, PartyAddress, Balance }
    bankList: [],
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        requestItems: false,
        itemGrid: false,
        partyDetails: false,
        bankList: false,
        save: false,
    },

    errors: {
        requestItems: null,
        itemGrid: null,
        partyDetails: null,
        bankList: null,
        save: null,
    },
};

const assetSaleReceiptSlice = createSlice({
    name: 'assetSaleReceipt',
    initialState,
    reducers: {
        clearItemGrid: (state) => {
            state.itemGrid = [];
            state.errors.itemGrid = null;
        },
        clearPartyDetails: (state) => {
            state.partyDetails = null;
            state.errors.partyDetails = null;
        },
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        resetAssetSaleReceipt: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRequestItems.pending, (state) => {
                state.loading.requestItems = true;
                state.errors.requestItems = null;
            })
            .addCase(fetchRequestItems.fulfilled, (state, action) => {
                state.loading.requestItems = false;
                state.requestItems = action.payload?.Data || [];
            })
            .addCase(fetchRequestItems.rejected, (state, action) => {
                state.loading.requestItems = false;
                state.errors.requestItems = action.payload;
                state.requestItems = [];
            });

        builder
            .addCase(fetchAssetSaleItemRecDetails.pending, (state) => {
                state.loading.itemGrid = true;
                state.errors.itemGrid = null;
            })
            .addCase(fetchAssetSaleItemRecDetails.fulfilled, (state, action) => {
                state.loading.itemGrid = false;
                state.itemGrid = action.payload?.Data || [];
            })
            .addCase(fetchAssetSaleItemRecDetails.rejected, (state, action) => {
                state.loading.itemGrid = false;
                state.errors.itemGrid = action.payload;
                state.itemGrid = [];
            });

        builder
            .addCase(fetchAssetPartyDetails.pending, (state) => {
                state.loading.partyDetails = true;
                state.errors.partyDetails = null;
            })
            .addCase(fetchAssetPartyDetails.fulfilled, (state, action) => {
                state.loading.partyDetails = false;
                const data = action.payload?.Data || [];
                state.partyDetails = data.length > 0 ? data[data.length - 1] : null;
            })
            .addCase(fetchAssetPartyDetails.rejected, (state, action) => {
                state.loading.partyDetails = false;
                state.errors.partyDetails = action.payload;
                state.partyDetails = null;
            });

        builder
            .addCase(fetchBankDetails.pending, (state) => {
                state.loading.bankList = true;
                state.errors.bankList = null;
            })
            .addCase(fetchBankDetails.fulfilled, (state, action) => {
                state.loading.bankList = false;
                state.bankList = action.payload?.Data || [];
            })
            .addCase(fetchBankDetails.rejected, (state, action) => {
                state.loading.bankList = false;
                state.errors.bankList = action.payload;
                state.bankList = [];
            });

        builder
            .addCase(saveAssetSaleReceiptData.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveAssetSaleReceiptData.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                const [status] = responseStr.split(',');

                if (status === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveAssetSaleReceiptData.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearItemGrid,
    clearPartyDetails,
    clearSaveResult,
    resetAssetSaleReceipt,
} = assetSaleReceiptSlice.actions;

// Selectors
// =========
export const selectRequestItemsArray = (state) => {
    const items = state.assetSaleReceipt.requestItems;
    return Array.isArray(items) ? items : [];
};
export const selectRequestItemsLoading = (state) => state.assetSaleReceipt.loading.requestItems;

export const selectItemGridArray = (state) => {
    const items = state.assetSaleReceipt.itemGrid;
    return Array.isArray(items) ? items : [];
};
export const selectItemGridLoading = (state) => state.assetSaleReceipt.loading.itemGrid;

export const selectPartyDetails = (state) => state.assetSaleReceipt.partyDetails;
export const selectPartyDetailsLoading = (state) => state.assetSaleReceipt.loading.partyDetails;

export const selectBankListArray = (state) => {
    const banks = state.assetSaleReceipt.bankList;
    return Array.isArray(banks) ? banks : [];
};
export const selectBankListLoading = (state) => state.assetSaleReceipt.loading.bankList;

export const selectSaveStatus = (state) => state.assetSaleReceipt.saveStatus;
export const selectSaveLoading = (state) => state.assetSaleReceipt.loading.save;
export const selectSaveError = (state) => state.assetSaleReceipt.errors.save;

export default assetSaleReceiptSlice.reducer;
